import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A pill button with an animated left→right progress fill for long async actions
 * (send) that have no granular progress events. While `active`, the fill eases
 * toward ~95% (asymptotic "almost there" feel); on `done` it snaps to 100%; on
 * reset it clears. The fill is a lighter overlay clipped by the pill's corners —
 * so the button itself shows that something is going through, no gray screen.
 */
export function ProgressButton({
  active,
  done = false,
  disabled = false,
  onClick,
  className = '',
  children,
  expectedMs = 12000,
}: {
  active: boolean;
  done?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  children: ReactNode;
  /** Rough expected duration — tunes how fast the fill approaches its cap. */
  expectedMs?: number;
}) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (done) {
      setProgress(100);
      return;
    }
    if (!active) {
      setProgress(0);
      return;
    }
    const start = performance.now();
    const tau = expectedMs / 2.5;
    const id = window.setInterval(() => {
      const elapsed = performance.now() - start;
      setProgress(95 * (1 - Math.exp(-elapsed / tau)));
    }, 90);
    return () => window.clearInterval(id);
  }, [active, done, expectedMs]);

  const showFill = active || done;

  return (
    <button onClick={onClick} disabled={disabled} className={`relative overflow-hidden ${className}`}>
      {showFill && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 z-0 bg-gradient-to-r from-white/10 via-white/20 to-white/30 transition-[width] duration-300 ease-out"
          style={{ width: `${progress}%` }}
        >
          <span className="absolute inset-y-0 -right-1 w-6 bg-card/60 blur-[7px]" />
        </span>
      )}
      <span className="relative z-10 flex items-center justify-center gap-2">{children}</span>
    </button>
  );
}
